if (!customElements.get('product-form')) {
  customElements.define(
    'product-form',
    class ProductForm extends HTMLElement {
      constructor() {
        super();

        this.form = this.querySelector('form');
        this.variantIdInput.disabled = false;
        this.form.addEventListener('submit', this.onSubmitHandler.bind(this));
        this.cart = document.querySelector('cart-notification') || document.querySelector('cart-drawer');
        this.submitButton = this.querySelector('[type="submit"]');
        this.submitButtonText = this.submitButton.querySelector('span');

        if (document.querySelector('cart-drawer')) this.submitButton.setAttribute('aria-haspopup', 'dialog');

        this.hideErrors = this.dataset.hideErrors === 'true';
        this.quantityInput = this.querySelector('input[name="quantity"]');
        this.refreshQuantityInputReference();
        this.boundHandleQuantityChange = this.handleQuantityChange.bind(this);
        this.boundHandleQuantityButtonClick = this.handleQuantityButtonClick.bind(this);
        this.quantityInput?.addEventListener('input', this.boundHandleQuantityChange);
        this.quantityInput?.addEventListener('change', this.boundHandleQuantityChange);
        this.quantityControl = this.quantityInput?.closest('quantity-input');
        this.quantityControl?.addEventListener('click', this.boundHandleQuantityButtonClick);
      }

      disconnectedCallback() {
        this.quantityInput?.removeEventListener('input', this.boundHandleQuantityChange);
        this.quantityInput?.removeEventListener('change', this.boundHandleQuantityChange);
        this.quantityControl?.removeEventListener('click', this.boundHandleQuantityButtonClick);
      }

      onSubmitHandler(evt) {
        evt.preventDefault();
        if (this.submitButton.getAttribute('aria-disabled') === 'true') return;
        this.refreshQuantityInputReference();
        if (!this.validateRequestedQuantity()) return;
        this.handleErrorMessage();

        this.submitButton.setAttribute('aria-disabled', true);
        this.submitButton.classList.add('loading');
        this.querySelector('.loading__spinner').classList.remove('hidden');

        const config = fetchConfig('javascript');
        config.headers['X-Requested-With'] = 'XMLHttpRequest';
        delete config.headers['Content-Type'];

        const formData = new FormData(this.form);
        if (this.cart) {
          formData.append(
            'sections',
            this.cart.getSectionsToRender().map((section) => section.id)
          );
          formData.append('sections_url', window.location.pathname);
          this.cart.setActiveElement(document.activeElement);
        }
        config.body = formData;

        fetch(`${routes.cart_add_url}`, config)
          .then((response) => response.json())
          .then((response) => {
            if (response.status) {
              publish(PUB_SUB_EVENTS.cartError, {
                source: 'product-form',
                productVariantId: formData.get('id'),
                errors: response.errors || response.description,
                message: response.message,
              });
              this.handleErrorMessage(response.description);
              this.error = true;

              const soldOutMessage = this.submitButton.querySelector('.sold-out-message');
              if (!soldOutMessage) return;
              this.submitButton.setAttribute('aria-disabled', true);
              this.submitButtonText.classList.add('hidden');
              soldOutMessage.classList.remove('hidden');
              return;
            } else if (!this.cart) {
              window.location = window.routes.cart_url;
              return;
            }

            const startMarker = CartPerformance.createStartingMarker('add:wait-for-subscribers');
            if (!this.error)
              publish(PUB_SUB_EVENTS.cartUpdate, {
                source: 'product-form',
                productVariantId: formData.get('id'),
                cartData: response,
              }).then(() => {
                CartPerformance.measureFromMarker('add:wait-for-subscribers', startMarker);
              });
            this.error = false;

            const quickAddModal = this.closest('quick-add-modal');
            if (quickAddModal) {
              document.body.addEventListener(
                'modalClosed',
                () => {
                  setTimeout(() => {
                    CartPerformance.measure("add:paint-updated-sections", () => {
                      this.cart.renderContents(response);
                    });
                  });
                },
                { once: true }
              );
              quickAddModal.hide(true);
            } else {
              CartPerformance.measure("add:paint-updated-sections", () => {
                this.cart.renderContents(response);
              });
            }
          })
          .catch((e) => {
            console.error(e);
          })
          .finally(() => {
            this.submitButton.classList.remove('loading');
            if (this.cart && this.cart.classList.contains('is-empty')) this.cart.classList.remove('is-empty');
            if (!this.error) this.submitButton.removeAttribute('aria-disabled');
            this.querySelector('.loading__spinner').classList.add('hidden');

            CartPerformance.measureFromEvent("add:user-action", evt);
          });
      }

      getRemainingInventory() {
        if (!this.quantityInput) return null;
        const inventoryAttr = this.quantityInput.dataset.inventoryQuantity;
        if (inventoryAttr == null || inventoryAttr === '') return null;

        const inventoryQty = parseInt(inventoryAttr, 10);
        const cartQty = parseInt(this.quantityInput.dataset.cartQuantity || '0', 10);
        if (isNaN(inventoryQty) || isNaN(cartQty)) return null;

        return Math.max(0, inventoryQty - cartQty);
      }

      refreshQuantityInputReference() {
        if (this.quantityInput && document.body.contains(this.quantityInput)) return;

        const sectionId = this.dataset.sectionId;
        if (sectionId) {
          this.quantityInput = document.getElementById(`Quantity-${sectionId}`);
        }

        if (!this.quantityInput && this.form?.id) {
          this.quantityInput = document.querySelector(`input[name="quantity"][form="${this.form.id}"]`);
        }
      }

      validateRequestedQuantity() {
        if (!this.quantityInput) return true;
        const requestedQty = parseInt(this.quantityInput.value || '0', 10);
        if (isNaN(requestedQty)) return true;
        const minAttr = this.quantityInput.getAttribute('min') || this.quantityInput.dataset.min;
        const minAllowed = minAttr ? parseInt(minAttr, 10) : null;

        if (minAllowed !== null && !isNaN(minAllowed) && requestedQty < minAllowed) {
          this.handleErrorMessage(`The minimum quantity for this item is ${minAllowed}.`);
          this.error = true;
          return false;
        }

        const maxAttr = this.quantityInput.getAttribute('max') || this.quantityInput.dataset.max;
        const maxAllowed = maxAttr ? parseInt(maxAttr, 10) : null;
        if (maxAllowed !== null && !isNaN(maxAllowed) && requestedQty > maxAllowed) {
          if (maxAllowed <= 0) {
            this.handleErrorMessage('The maximum quantity of this item is already in your cart.');
          } else {
            this.handleErrorMessage(`The maximum quantity allowed for this item is ${maxAllowed}.`);
          }
          this.error = true;
          return false;
        }

        const remaining = this.getRemainingInventory();
        if (remaining === null || requestedQty <= remaining) return true;

        if (remaining <= 0) {
          this.handleErrorMessage('The maximum quantity of this item is already in your cart.');
        } else {
          this.handleErrorMessage(`The maximum quantity allowed for this item is ${remaining}.`);
        }
        this.error = true;
        return false;
      }

      handleErrorMessage(errorMessage = false) {
        if (this.hideErrors) return;

        this.errorMessageWrapper =
          this.errorMessageWrapper || this.querySelector('.product-form__error-message-wrapper');
        if (!this.errorMessageWrapper) return;
        this.errorMessage = this.errorMessage || this.errorMessageWrapper.querySelector('.product-form__error-message');

        this.errorMessageWrapper.toggleAttribute('hidden', !errorMessage);

        if (errorMessage) {
          this.errorMessage.textContent = errorMessage;
        }
      }

      handleQuantityChange() {
        this.errorMessageWrapper =
          this.errorMessageWrapper || this.querySelector('.product-form__error-message-wrapper');
        if (!this.errorMessageWrapper || this.errorMessageWrapper.hasAttribute('hidden')) return;

        this.errorMessage = this.errorMessage || this.errorMessageWrapper.querySelector('.product-form__error-message');
        if (!this.errorMessage) return;

        const currentMessage = (this.errorMessage.textContent || '').toLowerCase();
        const isInventoryLimitMessage =
          currentMessage.includes('maximum quantity') ||
          currentMessage.includes('minimum quantity') ||
          currentMessage.includes('already in your cart') ||
          currentMessage.includes('due to availability') ||
          currentMessage.includes('were added to your cart');

        if (!isInventoryLimitMessage) return;
        const remaining = this.getRemainingInventory();
        const requestedQty = parseInt(this.quantityInput?.value || '0', 10);
        if (remaining !== null && !isNaN(requestedQty) && requestedQty > remaining) return;
        this.handleErrorMessage(false);

        const soldOutMessage = this.submitButton?.querySelector('.sold-out-message');
        if (soldOutMessage) {
          soldOutMessage.classList.add('hidden');
          this.submitButtonText?.classList.remove('hidden');
        }

        this.error = false;
        this.submitButton?.removeAttribute('aria-disabled');
        this.submitButton?.removeAttribute('disabled');
      }

      handleQuantityButtonClick(event) {
        if (!event.target.closest('.quantity__button')) return;
        window.requestAnimationFrame(() => this.handleQuantityChange());
      }

      toggleSubmitButton(disable = true, text) {
        if (disable) {
          this.submitButton.setAttribute('disabled', 'disabled');
          if (text) this.submitButtonText.textContent = text;
        } else {
          this.submitButton.removeAttribute('disabled');
          this.submitButtonText.textContent = window.variantStrings.addToCart;
        }
      }

      get variantIdInput() {
        return this.form.querySelector('[name=id]');
      }
    }
  );
}
